import { CloudOff, MobileScreenShare, RemoveRedEye } from '@mui/icons-material';
import {
  List,
  ListItem,
  ListItemIcon,
  MenuItem,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { brandTitleByKey } from '@prismamedia/one-brandkey';
import {
  NotificationTypeEnum,
  useNotification,
} from '@prismamedia/one-components';
import { map, property } from 'lodash';
import { FC, MouseEvent, memo, useMemo, useState } from 'react';
import {
  Link as RouterLink,
  generatePath,
  useHistory,
  useLocation,
} from 'react-router-dom';
import {
  ArticleFormat,
  ArticleHistory,
  ArticleStatus,
  GetArticlesDetailedQuery_query_articles_Article,
  GetArticlesQuery_query_articles_Article,
  GetArticlesQuery_query_articles_Article_categories_ArticleCategory,
  GetArticlesQuery_query_articles_Article_channels_ArticleChannel,
  SearchArticlesQuery_query_searchArticles_Article,
  NewsletterFlashSenderHistory,
} from '../../../../../__generated__/queries-web';
import {
  useArticleUpdater,
  useUpdateArticleLockerId,
} from '../../../../../apollo/mutations/article.web.graphql';
import { ArticleMenu } from '../../../../../components/ArticleMenu';
import { CardItem } from '../../../../../components/CardItem';
import { ListMediasIcons } from '../../../../../components/ListMediasIcons';
import { paths } from '../../../../../routing/Router/paths';
import { auth } from '../../../../../utils/auth';
import { getBrandOptions } from '../../../../../utils/brands';
import { formatDate } from '../../../../../utils/dateUtils';
import { getImageFromArticleBody } from '../../../../../utils/getImageFromArticleBody';
import {
  getImageFromMedias,
  getVideoThumbnail,
} from '../../../../../utils/media';
import { statusToLabel } from '../../../../../utils/statuses';
import { isOneWeb, standardizeArticleUrl } from '../../../../../utils/url';
import { useMediasIcons } from '../../../../../utils/useMediasIcons';
import { PAID_ARTICLE } from './constants';
import { ArticleItemStyleProps, useStyles } from './styles';

type ArticleItemProps = (
  | GetArticlesDetailedQuery_query_articles_Article
  | GetArticlesQuery_query_articles_Article
  | SearchArticlesQuery_query_searchArticles_Article
) & {
  brand?: string | null;
};

export const ArticleItem: FC<ArticleItemProps> = memo(
  ({
    id,
    title,
    status,
    publishedAt,
    updatedAt,
    events = [],
    categories = [],
    channels = [],
    creator,
    histories = [],
    brandKey,
    brand,
    format,
    medias,
    pushCount,
    live,
    canonical,
    lockedBy,
    qualifiers,
    newsletterFlashSenderHistories,
    ...rest
  }) => {
    const body =
      'body' in rest && rest.body !== undefined ? rest.body : undefined;
    const [upsert, { loading }] = useArticleUpdater();
    const { pushNotification } = useNotification();
    const theme = useTheme();
    const isSmallDevice = useMediaQuery(theme.breakpoints.down('md'));
    const updateLockerId = useUpdateArticleLockerId();
    const classes = useStyles() as ArticleItemStyleProps;
    const currentBrand = brand || '';
    const articleLink =
      format === ArticleFormat.Slideshow
        ? `${generatePath(paths.SLIDESHOW_EDIT, { id })}${
            currentBrand !== '' ? `?brand=${currentBrand}` : ''
          }`
        : generatePath(paths.ARTICLE_EDIT, {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            brandKey: brandKey!,
            articleType: format === ArticleFormat.Rich ? 'article' : 'video',
            search: brand ? 'search' : undefined,
            id,
          });
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const history = useHistory();
    const open = Boolean(anchorEl);
    const isLocked = lockedBy && lockedBy.id === auth.user?.id;

    const handleClose = (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setAnchorEl(null);
    };

    const handleMutation = async (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setAnchorEl(null);

      const props = {
        variables: { where: { id }, data: { status: ArticleStatus.Draft } },
      };
      const result = await upsert(props).catch(() => {
        pushNotification({
          message: 'Erreur durant la modification du status',
          type: NotificationTypeEnum.error,
        });
      });
      if (result?.data?.updateArticle) {
        pushNotification({
          message:
            format === ArticleFormat.Video
              ? `L'article vidéo a été supprimé`
              : 'Le status est passé en brouillon',
          type: NotificationTypeEnum.success,
        });
      }
    };
    const articleUsers = histories?.filter(
      (hist) => hist?.user?.__typename === 'OneUser' && 'name' in hist.user,
    ) as ArticleHistory[];

    const articleCreator = creator?.name || 'Inconnu';

    const articleModificator =
      articleUsers && articleUsers.length > 0
        ? articleUsers[articleUsers.length - 1].user?.name
        : 'Inconnu';

    const lastHistoryDate = histories?.[histories.length - 1]?.createdAt;
    const lastModifiedDate =
      lastHistoryDate && updatedAt && lastHistoryDate >= updatedAt
        ? lastHistoryDate
        : updatedAt;

    const image = useMemo(() => {
      switch (format) {
        case ArticleFormat.Slideshow:
          return getImageFromArticleBody(body, true);
        case ArticleFormat.Video:
          return getVideoThumbnail(medias);
        default:
          return getImageFromMedias(medias).mediaForDisplay;
      }
    }, [format, body, medias]);

    const displayTitle = (
      articleData:
        | GetArticlesQuery_query_articles_Article_channels_ArticleChannel[]
        | GetArticlesQuery_query_articles_Article_categories_ArticleCategory[],
    ) => map(articleData, property('category.title'));

    const categoryTitle: any = useMemo(() => {
      const _title =
        format === ArticleFormat.Video
          ? displayTitle(channels)
          : displayTitle(categories);
      if (!_title.length) {
        return null;
      }
      return _title.length === 2 ? `${_title[0]} / ${_title[1]}` : _title[0];
    }, [format, categories, channels]);

    const eventTitle = useMemo(() => {
      if (events.length > 0) {
        return events.length === 2
          ? `${events[0].category.title}, ${events[1].category.title}`
          : events[0].category.title;
      }
    }, [events]);

    const { pathname } = useLocation();
    const displayUnPublish = status !== 'Draft';
    const displayPushNotification = isOneWeb(pathname);

    const publicUrl = standardizeArticleUrl(canonical) || '';
    const displaySeeArticle = !!publicUrl;
    const isPublished = status === ArticleStatus.Published;
    const isScheduled = status === ArticleStatus.Scheduled;

    const handleMobilePush = (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      history.push(`/${brandKey}/mobilepush/add?id=${id}`);
    };

    const UnPublishItem = () => {
      const coreItem = (
        <>
          <ListItemIcon>
            <CloudOff fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Dépublier</Typography>
        </>
      );

      return isSmallDevice ? (
        <ListItem button data-testid="unPublish" onClick={handleMutation}>
          {coreItem}
        </ListItem>
      ) : (
        <MenuItem data-testid="unPublish" onClick={handleMutation}>
          {coreItem}
        </MenuItem>
      );
    };
    const SendPushItem = () => {
      const coreItem = (
        <>
          <ListItemIcon>
            <MobileScreenShare fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Envoyer un push</Typography>
        </>
      );
      return isSmallDevice ? (
        <ListItem button data-testid="pushButton" onClick={handleMobilePush}>
          {coreItem}
        </ListItem>
      ) : (
        <MenuItem data-testid="pushButton" onClick={handleMobilePush}>
          {coreItem}
        </MenuItem>
      );
    };
    const onClick = (event: MouseEvent<HTMLElement>) => {
      window.open(publicUrl);
      handleClose(event);
    };

    const DisplayItem = () => {
      const coreItem = (
        <>
          <ListItemIcon>
            <RemoveRedEye fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">Voir en front</Typography>
        </>
      );
      return isSmallDevice ? (
        <ListItem button data-testid="seeArticle" onClick={onClick}>
          {coreItem}
        </ListItem>
      ) : (
        <MenuItem data-testid="seeArticle" onClick={onClick}>
          {coreItem}
        </MenuItem>
      );
    };

    const mediasIcons = useMediasIcons(medias, live?.postCount || 0, pushCount);
    const isPaidArticle = qualifiers?.some(
      ({ qualifier }) => qualifier.title === PAID_ARTICLE,
    );

    return (
      <RouterLink
        to={articleLink}
        className={classes.link}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <CardItem
          titleOverflow={true}
          newsletterFlashSenderHistories={
            newsletterFlashSenderHistories as NewsletterFlashSenderHistory[]
          }
          hasPush={Boolean(pushCount)}
          pushCount={pushCount}
          isPaidArticle={isPaidArticle}
          ActionsMenu={
            (isPublished || isScheduled) && (
              <ArticleMenu
                displayPushNotification={displayPushNotification}
                displaySeeArticle={displaySeeArticle}
                displayUnPublish={displayUnPublish}
                articleId={id}
                publicUrl={publicUrl}
                brandKey={brandKey}
                articleFormat={format}
              />
            )
          }
          Icons={<ListMediasIcons listMedias={mediasIcons} />}
          id={id}
          title={title}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          media={image!}
          format={format}
          topTitle={`${articleCreator} - ${statusToLabel(format)[status]} ${
            publishedAt
              ? `le ${formatDate(new Date(publishedAt), 'dd/MM/yyyy à HH:mm')}`
              : ''
          } ${
            lastModifiedDate
              ? `(modifié le ${formatDate(
                  new Date(lastModifiedDate),
                  'dd/MM/yyy à HH:mm',
                )} par ${articleModificator})`
              : ''
          }`}
          brandKey={brandTitleByKey[brandKey]}
          eventTitle={eventTitle}
          categoryTitle={categoryTitle}
          lockedBy={lockedBy}
          isLocked={Boolean(isLocked)}
          updateLocker={updateLockerId}
        />
        {!loading && (displayUnPublish || displaySeeArticle) && isSmallDevice && (
          <SwipeableDrawer
            anchor="bottom"
            open={open}
            onOpen={() => {}}
            onClose={handleClose}
          >
            <List>
              {displayUnPublish && <UnPublishItem />}
              {displaySeeArticle && <DisplayItem />}
              {!!getBrandOptions(brandKey).pushPlatforms?.length &&
                displayPushNotification && <SendPushItem />}
            </List>
          </SwipeableDrawer>
        )}
      </RouterLink>
    );
  },
);
